<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'

import {
  VIDEO_EXPORT_FORMAT_OPTIONS,
  VIDEO_EXPORT_FPS_OPTIONS,
  type VideoExportFormatId,
  type VideoExportFps,
} from '@/rendering/export/exportPresets'
import { useMockupEditorStore } from '@/stores/mockupEditor'

const store = useMockupEditorStore()
const {
  exportFps,
  exportFormat,
  isExportingVideo,
  isExportPreflight,
  exportVideoProgress,
  hasAnimationLayers,
  backgroundMode,
} = storeToRefs(store)

/** 无画布模型层时禁用导出，避免空场景误操作 */
const exportActionsDisabled = computed(
  () => !hasAnimationLayers.value || isExportingVideo.value || isExportPreflight.value,
)

/**
 * 透明背景 + MP4 时禁用视频导出：浏览器端 H.264 管线不含 alpha。
 * 透明 + WebM 仍可导出（VP9 alpha），图片导出始终可用（PNG 支持透明）。
 */
const mp4BlockedByTransparent = computed(
  () => backgroundMode.value === 'transparent' && exportFormat.value === 'mp4',
)
const exportVideoDisabled = computed(
  () => exportActionsDisabled.value || mp4BlockedByTransparent.value,
)
const exportVideoHint = computed(() =>
  mp4BlockedByTransparent.value
    ? store.tUi('inspectorExport.videoMp4TransparentBlocked')
    : undefined,
)

const formats: { id: VideoExportFormatId; label: string }[] = VIDEO_EXPORT_FORMAT_OPTIONS.map((id) => ({
  id,
  label: id.toUpperCase(),
}))

const fpsOptions: readonly VideoExportFps[] = VIDEO_EXPORT_FPS_OPTIONS
</script>

<template>
  <details class="acc" open>
    <summary class="sum">{{ store.tUi('inspectorExport.title') }}</summary>
    <div class="body">
      <div class="grid2">
        <div class="field">
          <label class="lbl" for="fmt">{{ store.tUi('inspectorExport.exportFormat') }}</label>
          <select id="fmt" v-model="exportFormat" class="sel">
            <option v-for="x in formats" :key="x.id" :value="x.id">{{ x.label }}</option>
          </select>
        </div>
        <div class="field">
          <label class="lbl" for="fps">{{ store.tUi('inspectorExport.frameRate') }}</label>
          <select id="fps" v-model.number="exportFps" class="sel">
            <option v-for="f in fpsOptions" :key="f" :value="f">{{ f }}</option>
          </select>
        </div>
      </div>

      <div class="lbl2">{{ store.tUi('inspectorExport.operation') }}</div>
      <div v-if="isExportingVideo" class="prog-wrap" aria-live="polite">
        <div class="prog-bar" :style="{ width: `${Math.round(exportVideoProgress * 100)}%` }" />
        <span class="prog-lbl">{{ Math.round(exportVideoProgress * 100) }}%</span>
      </div>
      <div class="btns">
        <button
          type="button"
          class="btn secondary"
          :disabled="exportActionsDisabled"
          @click="store.exportImage"
        >
          {{ store.tUi('inspectorExport.exportImage') }}
        </button>
        <button
          type="button"
          class="btn primary"
          :disabled="exportVideoDisabled"
          :title="exportVideoHint"
          @click="store.exportVideo"
        >
          {{ store.tUi('inspectorExport.exportVideo') }}
        </button>
      </div>
      <p v-if="mp4BlockedByTransparent" class="hint alert">
        {{ store.tUi('inspectorExport.videoMp4TransparentBlocked') }}
      </p>
      <button v-if="isExportingVideo" type="button" class="btn ghost" @click="store.cancelExportVideo">
        {{ store.tUi('inspectorExport.cancelExport') }}
      </button>
    </div>
  </details>
</template>

<style scoped>
.acc {
  border: 0;
}

.sum {
  cursor: pointer;
  list-style: none;
  font-size: 0.95rem;
  font-weight: 900;
  letter-spacing: -0.02em;
  color: var(--im-text);
  padding: 0.15rem 0;
}

.sum::-webkit-details-marker {
  display: none;
}

.body {
  display: grid;
  gap: 0.75rem;
  padding-top: 0.65rem;
}

.grid2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.55rem;
}

.field {
  display: grid;
  gap: 0.35rem;
}

.lbl,
.lbl2 {
  font-size: 0.75rem;
  font-weight: 800;
  color: var(--im-muted);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.sel {
  width: 100%;
  border-radius: 10px;
  border: 1px solid var(--im-border);
  background: var(--im-surface);
  color: var(--im-text);
  padding: 0.55rem 0.65rem;
  font-size: 0.8125rem;
  font-weight: 700;
  outline: none;
}

.sel:focus-visible {
  border-color: var(--im-accent);
  box-shadow: 0 0 0 3px var(--im-focus-ring);
}

.btns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
}

.btn {
  border-radius: 10px;
  border: 1px solid transparent;
  padding: 0.62rem 0.65rem;
  font-weight: 850;
  font-size: 0.8125rem;
  cursor: pointer;
}

.btn:focus-visible {
  outline: 2px solid var(--im-accent);
  outline-offset: 2px;
}

.btn.primary {
  background: var(--im-interactive-bg);
  color: var(--im-interactive-fg);
  border-color: var(--im-interactive-bg);
}

.btn.primary:hover {
  filter: brightness(1.05);
}

.btn.secondary {
  border-color: var(--im-border);
  background: var(--im-surface);
  color: var(--im-text);
}

.btn.secondary:hover {
  background: var(--im-elevated);
}

.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.btn.ghost {
  width: 100%;
  border: 1px dashed var(--im-border);
  background: transparent;
  color: var(--im-muted);
}

.prog-wrap {
  position: relative;
  height: 8px;
  border-radius: 999px;
  background: var(--im-elevated);
  overflow: hidden;
}

.prog-bar {
  height: 100%;
  background: var(--im-interactive-bg);
  transition: width 0.12s ease-out;
}

.prog-lbl {
  position: absolute;
  right: 0.35rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.65rem;
  font-weight: 800;
  color: var(--im-muted);
  pointer-events: none;
}

.hint {
  margin: 0;
  font-size: 0.72rem;
  line-height: 1.5;
  color: var(--im-muted);
}

.hint.alert {
  color: #b45309;
  font-weight: 700;
}
</style>
